import { useMemo } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { FeeRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList, SkeletonMetrics } from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatMoney } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { combine, useAsync } from '@/lib/useAsync'

/** Every fee and interest charge, grouped by kind, with the annual total. */
export function FeesScreen() {
  const navigate = useNavigate()
  const fees = useAsync(() => api.analysis.fees(12), [])
  const recommendations = useAsync(() => api.recommendations.list({ theme: 'fees' }), [])

  const groups = fees.state.data ?? []
  const currency = groups[0]?.total.currency ?? 'USD'

  const totals = useMemo(() => {
    const year = groups.reduce((sum, group) => sum + Math.abs(group.total.amount), 0)
    const month = groups.reduce((sum, group) => sum + Math.abs(group.totalThisMonth?.amount ?? 0), 0)
    const avoidable = groups
      .filter((group) => group.avoidable)
      .reduce((sum, group) => sum + Math.abs(group.total.amount), 0)
    return { year, month, avoidable }
  }, [groups])

  const metrics: MetricSpec[] = [
    { key: 'year', label: t('fees.metric.year'), value: formatMoney({ amount: totals.year, currency }, { absolute: true }) },
    { key: 'month', label: t('fees.metric.month'), value: formatMoney({ amount: totals.month, currency }, { absolute: true }) },
    {
      key: 'avoidable',
      label: t('fees.metric.avoidable'),
      value: formatMoney({ amount: totals.avoidable, currency }, { absolute: true }),
      tone: totals.avoidable > 0 ? 'caution' : 'default',
    },
  ]

  const loaded = combine(fees.state, recommendations.state)

  const screenState =
    fees.state.status === 'error'
      ? 'error'
      : fees.state.status === 'loading' && !fees.state.data
        ? 'loading'
        : groups.length === 0
          ? 'empty'
          : 'ready'

  const avoidable = groups.filter((group) => group.avoidable)
  const unavoidable = groups.filter((group) => !group.avoidable)

  return (
    <Screen id="fees" state={screenState}>
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonMetrics count={3} />
          <SkeletonList rows={6} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="fees.error.title"
          error={loaded.status === 'error' ? loaded.error : undefined}
          onRetry={fees.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="fees.empty.title" />
      ) : (
        <div className="stack">
          <MetricRow metrics={metrics} />

          {(recommendations.state.data ?? []).slice(0, 2).map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSelect={() => navigate('recommendationDetail', { id: rec.id })}
            />
          ))}

          {avoidable.length > 0 ? (
            <section>
              <SectionHeader title={t('fees.metric.avoidable')} />
              <List>
                {avoidable.map((group) => (
                  <FeeRow
                    key={group.kind ?? 'other'}
                    fee={group}
                    onSelect={() => navigate('transactions', {}, { query: { filter: 'fees' } })}
                    onAccountSelect={
                      group.accountIds?.[0]
                        ? () => navigate('accountDetail', { id: group.accountIds![0] })
                        : undefined
                    }
                    onTransactionSelect={
                      group.transactionIds?.[0]
                        ? () => navigate('transactionDetail', { id: group.transactionIds![0] })
                        : undefined
                    }
                  />
                ))}
              </List>
            </section>
          ) : null}

          {unavoidable.length > 0 ? (
            <section>
              <SectionHeader title={t('fees.title')} />
              <List>
                {unavoidable.map((group) => (
                  <FeeRow
                    key={group.kind ?? 'other'}
                    fee={group}
                    onSelect={() => navigate('transactions', {}, { query: { filter: 'fees' } })}
                    onAccountSelect={
                      group.accountIds?.[0]
                        ? () => navigate('accountDetail', { id: group.accountIds![0] })
                        : undefined
                    }
                    onTransactionSelect={
                      group.transactionIds?.[0]
                        ? () => navigate('transactionDetail', { id: group.transactionIds![0] })
                        : undefined
                    }
                  />
                ))}
              </List>
            </section>
          ) : null}
        </div>
      )}
    </Screen>
  )
}
